/* ============================================================
   STICKR LAB — Global visual interactions
   VISUAL ONLY. No pricing, cart, upload, or account logic.
   Everything is data-attribute driven so markup stays declarative
   and later functional wiring can hook the same hooks.
   ============================================================ */
(function () {
  'use strict';

  /* ---- Selectable option groups (size / shape / border / qty, tabs) ----
     Markup:
       <div data-sl-select>
         <button data-sl-option class="is-selected">...</button>
         <button data-sl-option>...</button>
       </div>
     Optional: data-sl-select="multi" to allow multiple selections.
     Fires a `sl:change` event on the group with { value, el }.
  */
  function initSelectGroups(root) {
    root.querySelectorAll('[data-sl-select]').forEach(function (group) {
      if (group.__slBound) return;
      group.__slBound = true;
      var multi = group.getAttribute('data-sl-select') === 'multi';

      group.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-sl-option]');
        if (!opt || !group.contains(opt)) return;
        if (opt.hasAttribute('disabled')) return;

        if (multi) {
          opt.classList.toggle('is-selected');
        } else {
          group.querySelectorAll('[data-sl-option]').forEach(function (o) {
            o.classList.remove('is-selected');
            o.setAttribute('aria-selected', 'false');
          });
          opt.classList.add('is-selected');
          opt.setAttribute('aria-selected', 'true');
        }

        group.dispatchEvent(new CustomEvent('sl:change', {
          bubbles: true,
          detail: { value: opt.getAttribute('data-value'), el: opt }
        }));
      });
    });
  }

  /* ---- Tab panels ----
     Markup:
       <div data-sl-tabs>
         <button data-sl-tab="a" class="is-selected">A</button>
         <button data-sl-tab="b">B</button>
       </div>
       <div data-sl-panel="a">...</div>
       <div data-sl-panel="b" hidden>...</div>
  */
  function initTabs(root) {
    root.querySelectorAll('[data-sl-tabs]').forEach(function (tabs) {
      if (tabs.__slBound) return;
      tabs.__slBound = true;
      var scope = tabs.closest('[data-sl-tabs-scope]') || document;

      tabs.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-sl-tab]');
        if (!tab) return;
        var key = tab.getAttribute('data-sl-tab');

        tabs.querySelectorAll('[data-sl-tab]').forEach(function (t) {
          t.classList.toggle('is-selected', t === tab);
        });
        scope.querySelectorAll('[data-sl-panel]').forEach(function (p) {
          p.hidden = p.getAttribute('data-sl-panel') !== key;
        });
      });
    });
  }

  /* ---- Dropdowns / popover menus ----
     Markup:
       <div data-sl-dropdown>
         <button data-sl-dropdown-trigger>...</button>
         <div data-sl-dropdown-menu hidden>...</div>
       </div>
  */
  function initDropdowns(root) {
    root.querySelectorAll('[data-sl-dropdown]').forEach(function (dd) {
      if (dd.__slBound) return;
      dd.__slBound = true;
      var trigger = dd.querySelector('[data-sl-dropdown-trigger]');
      var menu = dd.querySelector('[data-sl-dropdown-menu]');
      if (!trigger || !menu) return;

      function close() {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      }
      function open() {
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        menu.hidden ? open() : close();
      });
      document.addEventListener('click', function (e) {
        if (!dd.contains(e.target)) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ---- Accordion (single-open) ----
     Markup:
       <div data-sl-accordion>
         <div data-sl-acc class="is-open">
           <button data-sl-acc-head>...</button>
           <div class="...__body">...</div>
         </div>
       </div>
     Add `data-sl-accordion="multi"` to allow several open at once.
  */
  function initAccordions(root) {
    root.querySelectorAll('[data-sl-accordion]').forEach(function (group) {
      if (group.__slBound) return;
      group.__slBound = true;
      var multi = group.getAttribute('data-sl-accordion') === 'multi';

      group.addEventListener('click', function (e) {
        var head = e.target.closest('[data-sl-acc-head]');
        if (!head || !group.contains(head)) return;
        var item = head.closest('[data-sl-acc]');
        if (!item) return;
        var willOpen = !item.classList.contains('is-open');
        if (!multi) {
          group.querySelectorAll('[data-sl-acc]').forEach(function (i) { i.classList.remove('is-open'); });
        }
        item.classList.toggle('is-open', willOpen);
      });
    });
  }

  /* ---- Mobile nav toggle ----
     [data-sl-nav-toggle] toggles [data-sl-mobile-nav]
  */
  function initMobileNav(root) {
    root.querySelectorAll('[data-sl-nav-toggle]').forEach(function (btn) {
      if (btn.__slBound) return;
      btn.__slBound = true;
      var target = document.querySelector(btn.getAttribute('data-sl-nav-toggle') || '[data-sl-mobile-nav]');
      if (!target) return;
      btn.addEventListener('click', function () {
        var open = target.hasAttribute('hidden');
        if (open) target.removeAttribute('hidden'); else target.setAttribute('hidden', '');
        document.body.classList.toggle('sl-no-scroll', open);
      });
    });
  }
  function initAll(root) {
    root = root || document;
    initSelectGroups(root);
    initTabs(root);
    initDropdowns(root);
    initAccordions(root);
    initMobileNav(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when the Shopify theme editor injects/reloads a section.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.StickrLab = window.StickrLab || {};
  window.StickrLab.init = initAll;
})();
